import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Crown, Ticket, XCircle, Loader2, LogIn, Zap, ShieldCheck, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AccountPage = () => {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isCancelling, setIsCancelling] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  const isStaff = !!user?.is_staff;
  const isPro = user?.is_pro || user?.plan === 'lifetime' || user?.plan === 'monthly' || isStaff;
  const credits = user?.remaining_downloads || 0;
  const isPasswordAccount = user?.auth_provider === 'password';

  const handleCancel = async () => {
    if (!window.confirm('Cancel your Pro subscription? This will end your unlimited access.')) return;
    setIsCancelling(true);
    try {
      const res = await axios.post(`${API}/payments/cancel-subscription`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Subscription cancelled');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold mb-2">Account &amp; Billing</h1>
          <p className="text-sm text-muted-foreground mb-6">Sign in to manage your plan and downloads.</p>
          <Button onClick={() => navigate('/login')} data-testid="account-signin-btn">
            <LogIn className="w-4 h-4 mr-2" /> Sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-lg mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8" data-testid="account-title">Account &amp; Billing</h1>

        <div className="bg-card border border-border rounded-xl p-5 mb-4" data-testid="account-profile">
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                {(user.name || '?')[0]}
              </div>
            )}
            <div>
              <p className="font-medium">{user.name}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 mb-4" data-testid="account-plan">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-muted-foreground">Current plan</p>
            {isPro ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                <Crown className="w-3 h-3" /> {isStaff ? 'STAFF' : 'PRO'}
              </span>
            ) : (
              <span className="text-[11px] font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">FREE</span>
            )}
          </div>
          <p className="text-xl font-semibold mb-4" data-testid="account-plan-name">
            {isStaff ? 'Staff — Unlimited access' : isPro ? 'Pro Unlimited — R49/mo' : 'Free — 1 download per day'}
          </p>
          {isStaff ? (
            <p className="text-sm text-muted-foreground">Staff accounts bypass all paywalls. Contact an admin to change this.</p>
          ) : isPro ? (
            <Button
              variant="outline"
              className="w-full text-red-500 border-red-500/30 hover:bg-red-500/5"
              onClick={handleCancel}
              disabled={isCancelling}
              data-testid="account-cancel-btn"
            >
              {isCancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Cancel Subscription
            </Button>
          ) : (
            <Button className="w-full" onClick={() => navigate('/pricing')} data-testid="account-upgrade-btn">
              <Zap className="w-4 h-4 mr-2" /> Upgrade to Pro — R49/mo
            </Button>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-5" data-testid="account-credits">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Remaining downloads</p>
              <p className="text-xl font-semibold flex items-center gap-2">
                <Ticket className="w-5 h-5 text-blue-500" />
                <span data-testid="account-credits-count">{isPro ? 'Unlimited' : credits}</span>
              </p>
            </div>
            {!isPro && (
              <Button variant="outline" size="sm" onClick={() => navigate('/pricing')} data-testid="account-buy-credit-btn">
                Buy for R8
              </Button>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 mt-4" data-testid="account-security">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <h2 className="text-base font-semibold">Security</h2>
            </div>
          </div>
          {isPasswordAccount ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Password</p>
                <p className="text-xs text-muted-foreground">Change the password used to sign in.</p>
              </div>
              <Button variant="outline" onClick={() => setShowChangePw(true)} data-testid="change-password-btn">
                <KeyRound className="w-4 h-4 mr-2" /> Change Password
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              You signed in with Google, so there&apos;s no password on this account.
              Use <button type="button" onClick={() => navigate('/login')} className="text-primary hover:underline">Forgot password</button> to add one.
            </p>
          )}
        </div>
      </div>
      <ChangePasswordModal open={showChangePw} onClose={() => setShowChangePw(false)} />
    </div>
  );
};

export default AccountPage;
